import { AlertCircle, Inbox, Loader2, Lock, RefreshCw, WifiOff } from 'lucide-react';
import { Button } from './Button.jsx';
import { Icon } from './Icon.jsx';
import { cn } from '../lib/cn.js';

/**
 * What a region shows when it has no data to show. Three different facts, three components —
 * an error is never drawn as "nothing here", and nothing is ever drawn as zero.
 */

export function EmptyState({ icon = Inbox, title, body, action, compact = false, className }) {
  return (
    <div className={cn('flex flex-col items-center justify-center text-center', compact ? 'gap-1.5 px-4 py-6' : 'gap-2 px-6 py-12', className)}>
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-surface-muted text-ink-faint">
        <Icon icon={icon} size={20} />
      </span>
      <div className="text-md font-medium text-ink-strong">{title}</div>
      {body ? <p className="max-w-sm text-base text-ink-subtle">{body}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

/**
 * ErrorState — what failed, in the server's own words (ApiError.message), and a way to retry.
 * A 403 says "you don't have access" rather than "something went wrong".
 */
export function ErrorState({ error, title, onRetry, compact = false, className }) {
  const kind = error?.kind;
  const icon = kind === 'network' || kind === 'timeout' ? WifiOff : kind === 'forbidden' ? Lock : AlertCircle;
  const heading = title || (kind === 'forbidden' ? 'You don’t have access to this' : kind === 'network' || kind === 'timeout' ? 'Couldn’t reach the server' : 'This didn’t load');
  return (
    <div role="alert" className={cn('flex flex-col items-center justify-center text-center', compact ? 'gap-1.5 px-4 py-6' : 'gap-2 px-6 py-12', className)}>
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-failed-bg text-failed-fg">
        <Icon icon={icon} size={20} />
      </span>
      <div className="text-md font-medium text-ink-strong">{heading}</div>
      {error?.message ? <p className="max-w-md break-words text-base text-ink-subtle">{error.message}</p> : null}
      {onRetry ? <Button size="sm" icon={RefreshCw} onClick={() => onRetry()} className="mt-2">Try again</Button> : null}
    </div>
  );
}

export function LoadingState({ label = 'Loading', compact = false, className }) {
  return (
    <div role="status" className={cn('flex items-center justify-center gap-2 text-base text-ink-subtle', compact ? 'py-6' : 'py-12', className)}>
      <Icon icon={Loader2} size={16} className="animate-spin" />
      {label}
    </div>
  );
}

/** A grey shimmering bar standing in for text that is loading. */
export function Skeleton({ className }) {
  return <span aria-hidden="true" className={cn('skeleton block h-3.5 w-full', className)} />;
}

/**
 * QueryState — render a React Query result: loading → skeleton/spinner, error → ErrorState with
 * retry, empty → EmptyState, otherwise children(data).
 *   <QueryState query={q} empty={{ title: 'No payouts waiting' }} isEmpty={(d) => !d.rows.length}>
 *     {(data) => …}
 *   </QueryState>
 */
export function QueryState({ query, loading, empty, isEmpty, compact, children }) {
  if (query.isPending) return loading ?? <LoadingState compact={compact} />;
  if (query.isError) return <ErrorState error={query.error} onRetry={query.refetch} compact={compact} />;
  if (isEmpty?.(query.data)) return <EmptyState compact={compact} {...empty} />;
  return children(query.data);
}
